import React, { useState, useEffect } from 'react';
import { Fab, Zoom } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Navbar from './components/Navbar';
import AboutUs from './components/AboutUs';
import Hero from './components/Hero';
import Services from './components/Services';
import PatientInfo from './components/PatientInfo';
import ContactUs from './components/ContactUs';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';
import dentalTheme from '../../theme1';
import SmileGallery from './components/SmileGallery';
import Seo from '../../components/seo/Seo';
import StructuredData from '../../components/seo/StructuredData';
import BreadcrumbsNav from '../../components/BreadcrumbsNav';
import {
  siteUrl,
  logoUrl,
  phone,
  address,
  openingHoursSpecification,
  sameAs,
} from '../../components/seo/config';

const DentalPage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const dentalSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    name: "Drax Hall Family Dental",
    url: `${siteUrl}/dental`,
    logo: logoUrl,
    image: logoUrl,
    telephone: phone,
    address,
    openingHoursSpecification,
    sameAs,
    medicalSpecialty: ["Dentistry", "Pediatric", "Cosmetic"],
    areaServed: "JM",
  };
  const dentalFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What should I bring to my first appointment?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Bring your medical history, allergy information, and your insurance card. Arrive 5–10 minutes early.",
        },
      },
      {
        "@type": "Question",
        name: "What are the dos and don’ts after an extraction?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Do: rest, drink fluids, rinse with salt water after 12 hours. Don’t: smoke, drink carbonated beverages, or disturb the clot.",
        },
      },
      {
        "@type": "Question",
        name: "At what age should a child see a dentist?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Within six months of the first tooth erupting and no later than the first birthday.",
        },
      },
      {
        "@type": "Question",
        name: "How often should I visit the dentist?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "At least twice a year for check-ups, or sooner if you have pain or bleeding gums.",
        },
      },
      {
        "@type": "Question",
        name: "Why give fluoride treatment to children?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Fluoride strengthens teeth and helps prevent decay in developing teeth.",
        },
      },
      {
        "@type": "Question",
        name: "What causes cavities?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Poor oral hygiene, sugary foods, deep tooth crevices, dry mouth, and over-brushing with hard bristles.",
        },
      },
      {
        "@type": "Question",
        name: "Is teeth whitening effective and safe?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, when done correctly; results are not permanent.",
        },
      },
      {
        "@type": "Question",
        name: "Will my insurance cover this procedure?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Coverage depends on your provider; contact us to verify partial or full coverage.",
        },
      },
      {
        "@type": "Question",
        name: "How soon can I eat or drink after a cleaning?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Wait at least 30 minutes to an hour to allow teeth to re-harden.",
        },
      },
      {
        "@type": "Question",
        name: "How often should I floss?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "At least once a day; more frequent flossing is encouraged.",
        },
      },
      {
        "@type": "Question",
        name: "What causes bad breath?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Often from poor oral hygiene, food decay, or plaque buildup; regular brushing, flossing, and mouthwash help.",
        },
      },
    ],
  };
  // Track scrolling to toggle visibility of the scroll to top button
  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Seo
        title="Dental Care Services | Drax Hall Health Group"
        description="Family-friendly dental care in Drax Hall including cleanings, cosmetic dentistry, pediatric visits, and emergency treatments delivered by trusted clinicians."
        canonicalPath="/dental"
        ogImage="/dental-logo.png"
      />
      <StructuredData data={[dentalSchema, dentalFaq]} />
      <Navbar />
      <BreadcrumbsNav
        items={[
          { label: "Home", path: "/" },
          { label: "Family Dental", path: "/dental" },
        ]}
      />
      <Hero />
      <AboutUs />
      <Services />
      <Testimonials />
      <SmileGallery />
      <PatientInfo />
      <ContactUs />
      <Footer />

      {/* Scroll to Top Button */}
      <Zoom in={isVisible}>
        <Fab
          color="secondary"
          size="small"
          onClick={scrollToTop}
          aria-label="scroll back to top"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 100,
            backgroundColor: dentalTheme.palette.info.main,
            '&:hover': {
              backgroundColor: dentalTheme.palette.primary.main,
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </>
  );
};

export default DentalPage;
